import type {
  AnswerValue,
  LeadConversationDetailDto,
  MapaFrequency,
} from '@devmil/contracts';
import {useState} from 'react';
import {Link, useParams} from 'react-router-dom';

import {PageTitle} from '../components/PageTitle.js';
import {useLeadConversation} from '../features/leads/useLeadConversation.js';
import {WhatsappRedeemForm} from '../features/leads/WhatsappRedeemForm.js';
import {formatDateTime, UNAVAILABLE} from '../lib/format.js';
import {describeProblem} from '../lib/panel-problem.js';

/**
 * Detalhe de uma conversa do funil MAPA.
 *
 * Esta tela não aparece na prévia que o Thiago aprovou, então ela é criação de
 * verdade. Mesmo assim herda tokens e classes de `app/styles.css`: cor
 * inventada é o jeito mais rápido de a tela nova denunciar que nasceu de outra
 * mão.
 *
 * O que ela mostra sobre a pessoa: pseudônimo e, quando existe, os 4 últimos
 * dígitos do telefone. IGSID, @ do Instagram e texto cru da Direct não passam
 * pelo contrato, e as respostas em texto ficam ocultas até alguém pedir para
 * ver: uma tela aberta numa reunião não pode expor o que o titular escreveu.
 */

const ESTAGIO_LABEL: Record<LeadConversationDetailDto['stage'], string> = {
  idle: 'Sem conversa',
  notice_pending: 'Aviso pendente',
  question_company_type: 'Pergunta 1 de 5',
  question_task: 'Pergunta 2 de 5',
  question_frequency: 'Pergunta 3 de 5',
  question_platforms: 'Pergunta 4 de 5',
  question_whatsapp: 'Pergunta 5 de 5',
  completed_without_whatsapp: 'Diagnóstico sem WhatsApp',
  link_issued: 'Link emitido',
  whatsapp_message_confirmed: 'Mensagem confirmada',
  channels_linked: 'Canais associados',
  human_handoff: 'Passado para atendimento humano',
  expired: 'Janela vencida',
  opted_out: 'Titular saiu',
};

const CONSENTIMENTO_LABEL: Record<
  LeadConversationDetailDto['whatsappConsent'],
  string
> = {
  not_asked: 'NÃO PEDIDO',
  granted: 'CONCEDIDO',
  revoked: 'REVOGADO',
};

const RETENCAO_LABEL: Record<
  LeadConversationDetailDto['retentionClass'],
  string
> = {
  incomplete: 'Incompleta',
  completed_unqualified: 'Concluída sem qualificação',
  qualified: 'Qualificada',
};

const PERGUNTA_LABEL: Record<AnswerValue['question'], string> = {
  company_type: 'Tipo de empresa',
  time_consuming_task: 'Tarefa que consome tempo',
  frequency: 'Frequência',
  platforms: 'Plataformas',
  whatsapp_permission: 'Autoriza WhatsApp',
};

const FREQUENCIA_LABEL: Record<MapaFrequency, string> = {
  daily: 'Diária',
  weekly: 'Semanal',
  monthly: 'Mensal',
  sporadic: 'Esporádica',
  other: 'Outra',
};

/**
 * O que a linha da resposta mostra.
 *
 * `frequency` e `whatsapp_permission` são enum e booleano: escolha de uma lista
 * fechada, não texto escrito pela pessoa, e por isso aparecem sempre. As outras
 * três são texto que o titular digitou na Direct, e ficam atrás do botão.
 */
const valorDaResposta = (answer: AnswerValue, revelado: boolean): string => {
  switch (answer.question) {
    case 'frequency':
      return FREQUENCIA_LABEL[answer.value];
    case 'whatsapp_permission':
      return answer.value ? 'Sim' : 'Não';
    case 'platforms':
      return revelado ? answer.value.join(', ') : 'OCULTO';
    default:
      return revelado ? answer.value : 'OCULTO';
  }
};

export const LeadConversationPage = () => {
  const {pseudonym = ''} = useParams<{pseudonym: string}>();
  const {data, isPending, error, refresh} = useLeadConversation(pseudonym);
  const [revelado, setRevelado] = useState(false);

  const cabecalho = (
    <PageTitle
      title="Conversa do funil MAPA"
      subtitle={`Referência ${pseudonym}. Nada além do pseudônimo identifica a pessoa.`}
      badge="PSEUDONIMIZADA"
    />
  );

  if (isPending) {
    return (
      <section data-page="lead-conversation">
        {cabecalho}
        <p className="dm-loading" role="status">
          Carregando a conversa…
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section data-page="lead-conversation">
        {cabecalho}
        <div className="dm-alert" role="alert">
          {describeProblem(error).message}
        </div>
        <div className="dm-actions">
          <Link className="dm-action" to="/mapa">
            Voltar ao funil
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section data-page="lead-conversation">
      {cabecalho}

      <div className="dm-actions">
        <Link className="dm-action" to="/mapa">
          Voltar ao funil
        </Link>
        <Link className="dm-action" to="/privacidade">
          Pedidos de privacidade
        </Link>
      </div>

      <div className="dm-ops-grid">
        <section className="dm-panel" aria-label="Estado da conversa">
          <div className="dm-panel-head">
            <h2>Estado</h2>
            <span className="dm-illustrative">
              {CONSENTIMENTO_LABEL[data.whatsappConsent]}
            </span>
          </div>
          <ul className="dm-checklist">
            <li>
              <span>Etapa</span>
              <strong>{ESTAGIO_LABEL[data.stage]}</strong>
            </li>
            <li>
              <span>Janela de resposta termina</span>
              <strong>{formatDateTime(data.windowEndsAt)}</strong>
            </li>
            <li>
              <span>Automação permitida</span>
              <strong
                className={data.automationAllowed ? 'dm-ok' : 'dm-ok is-failed'}
              >
                {data.automationAllowed ? 'SIM' : 'NÃO'}
              </strong>
            </li>
            <li>
              <span>Última interação</span>
              <strong>{formatDateTime(data.lastInteractionAt)}</strong>
            </li>
            <li>
              <span>Ciência do aviso</span>
              <strong>
                {/* Ciência do aviso NÃO é consentimento, e a tela não pode
                    juntar os dois: um diz que a pessoa leu, o outro que ela
                    autorizou. */}
                {data.noticeAcknowledgedAt
                  ? formatDateTime(data.noticeAcknowledgedAt)
                  : 'Sem registro'}
              </strong>
            </li>
            <li>
              <span>WhatsApp associado</span>
              <strong>
                {/* Nunca o telefone inteiro: o contrato só entrega 4 dígitos, e
                    quando não há vínculo isso é ausência, não zero. */}
                {data.phoneLast4 ? `FINAL ${data.phoneLast4}` : UNAVAILABLE}
              </strong>
            </li>
            <li>
              <span>Classe de retenção</span>
              <strong>{RETENCAO_LABEL[data.retentionClass]}</strong>
            </li>
            <li>
              <span>Expurgo programado</span>
              <strong>{formatDateTime(data.purgeAfter)}</strong>
            </li>
          </ul>
        </section>

        <WhatsappRedeemForm
          pseudonym={data.pseudonym}
          consent={data.whatsappConsent}
          onLinked={refresh}
        />
      </div>

      <section className="dm-panel" aria-label="Respostas do diagnóstico">
        <div className="dm-panel-head">
          <h2>Diagnóstico</h2>
          <span className="dm-illustrative">
            {`${data.answers.length} DE 5`}
          </span>
        </div>
        <ul className="dm-checklist">
          {data.answers.map((answer) => (
            <li key={answer.question}>
              <span>{PERGUNTA_LABEL[answer.question]}</span>
              <strong>{valorDaResposta(answer, revelado)}</strong>
            </li>
          ))}
        </ul>
        <div className="dm-actions">
          <button
            className="dm-action"
            type="button"
            aria-pressed={revelado}
            onClick={() => {
              setRevelado((atual) => !atual);
            }}
          >
            {revelado
              ? 'Ocultar o que o titular escreveu'
              : 'Mostrar o que o titular escreveu'}
          </button>
        </div>
      </section>
    </section>
  );
};
